import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

type EmptyStateProps = {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
  size?: "sm" | "md";
};

/**
 * Shared empty state for list pages, card bodies, and dashboard panels.
 * Prefer this over ad-hoc “Nenhuma…” paragraphs so copy and spacing stay consistent.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
  size = "md",
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "text-muted-foreground flex flex-col items-center justify-center text-center",
        size === "md" ? "gap-2 px-4 py-8" : "gap-1.5 px-3 py-5",
        className,
      )}
      data-slot="empty-state"
    >
      {Icon ? (
        <Icon
          aria-hidden="true"
          className={cn("text-muted-foreground/70", size === "md" ? "size-8" : "size-6")}
        />
      ) : null}
      <div className="space-y-1">
        <p
          className={cn(
            "text-foreground font-medium",
            size === "md" ? "text-sm" : "text-xs/relaxed",
          )}
        >
          {title}
        </p>
        {description ? <p className="text-xs/relaxed text-balance">{description}</p> : null}
      </div>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}
